// Ручные типы для backend-эндпоинтов. До генерации через `bun run openapi`
// (нужен поднятый бэк на :8080), используем эти.

export type Role = 'curator' | 'expert' | 'gip' | 'admin' | 'customer';

export interface User {
  id: string;
  email: string;
  full_name: string;
  default_role: Role;
  expert_section: string | null;
  expertise_id: string | null;
  is_active: boolean;
}

export interface MeResponse {
  user: User;
  auth_session_id: string;
  expires_at: string | null;
}

export interface LoginRequest {
  email: string;
  password: string;
}

/** Тип объекта проекта по ПП 87: 'areal' (Часть 2 — капитальное строительство)
 *  | 'linear' (Часть 3 — линейные объекты). null — не указан. */
export type ObjectType = 'areal' | 'linear';

// ── Phase 13: паспорт объекта (persistent project-level) ────────────────
export type ObjectSubtype = 'railway' | 'pipeline' | 'highway' | 'power_line' | 'telecom';
export type ProjectStage = 'P' | 'R' | 'PD' | 'RD';

export interface TepItem {
  name: string;
  value: string | number;
  unit?: string | null;
}

export interface PassportField<T = unknown> {
  value: T;
  confidence: 'auto' | 'user';
  source?: string[];
}

export interface ProjectPassport {
  object_type?: PassportField<ObjectType>;
  subtype?: PassportField<ObjectSubtype>;
  category_or_class?: PassportField<string>;
  project_code?: PassportField<string>;
  customer?: PassportField<string>;
  general_designer?: PassportField<string>;
  project_stage?: PassportField<ProjectStage>;
  region?: PassportField<string>;
  tep?: PassportField<TepItem[]>;
  construction_duration_months?: PassportField<number>;
  estimated_cost_rub?: PassportField<number>;
  functional_category?: PassportField<string>;
}

export interface PassportStateResponse {
  passport_json: ProjectPassport;
  passport_extracted_at: string | null;
}

/** 15.E: тариф проекта. null = не активирован. */
export type Tariff = 'express' | 'full' | null;

/** 15.E Reading B: прогоны по каждой проверке (per-check лимит express). */
export type CheckKind = 'completeness' | 'task' | 'norms' | 'tep';
export type RunsByCheck = Partial<Record<CheckKind, number>>;

export interface Project {
  id: string;
  name: string;
  status: string | null;
  created_at: string | null;
  expertise_id: string | null;
  curator_id: string | null;
  object_type?: ObjectType | null;
  deadline_at?: string | null;
  // 15.E — тариф + счётчики для гейтинга UI (Reading B: по каждой проверке).
  tariff?: Tariff;
  // Прогоны по каждой проверке: {"completeness": n, "task": n, "norms": n}.
  runs_by_check?: RunsByCheck;
  snapshots_count?: number; // число редакций (снапшотов)
}

/** Раздел в ответе GET /projects/{id}/compliance/pp87. */
export interface PP87Section {
  code: string;
  name: string;
  mandatory: boolean;
  status: 'present' | 'missing' | 'optional_missing';
  found_documents: Array<{ document_id: string; title: string; format: string | null }>;
}

export interface PP87ComplianceResponse {
  project_id: string;
  object_type: ObjectType | null;
  needs_object_type?: boolean;
  message?: string;
  sections?: PP87Section[];
  unmatched_documents?: Array<{ document_id: string; title: string }>;
  summary?: {
    total_sections: number;
    present: number;
    missing_mandatory: number;
    missing_optional: number;
    unmatched_documents: number;
  };
}

// ── Phase 14B: соответствие нормативам ────────────────────────────────
export type ComplianceStatus = 'ok' | 'gap' | 'partial' | 'unknown';

export interface ComplianceEvidence {
  kind: string;
  [key: string]: unknown;
}

export interface ComplianceRequirement {
  norm_code: string;
  section_number: string;
  section_title: string;
  requirement: string;
  scope: string[];
  evidence_in_pd: ComplianceEvidence[];
  compliance_status: ComplianceStatus;
  confidence: 'high' | 'medium' | 'low';
  note?: string;
  norm_chunk_preview?: string;
}

export interface ComplianceApplicableNorm {
  code: string;
  title: string;
  scope_match: string[];
  rationale?: string;
}

export interface ComplianceSummaryByNorm {
  ok: number;
  gap: number;
  partial: number;
  unknown: number;
}

export interface ComplianceResponse {
  project_id: string;
  object_type: string | null;
  subtype: string | null;
  category_or_class: string | null;
  applicable_norms: ComplianceApplicableNorm[];
  compliance: ComplianceRequirement[];
  summary: {
    total_requirements: number;
    ok: number;
    gap: number;
    partial: number;
    unknown: number;
    by_norm: Record<string, ComplianceSummaryByNorm>;
  };
  checked_at: string | null;
  from_cache?: boolean;
}

export interface ProjectsResponse {
  projects: Project[];
  role?: Role;
}

/** Снапшот (в UI отображается как «Редакция»). */
export interface Snapshot {
  id: string;
  content_hash?: string;
  kg_state_marker?: string;
  created_at: string;
  invalidated_at: string | null;
}
export interface SnapshotsResponse {
  snapshots: Snapshot[];
}

/** Lifecycle ingest версии документа. См. db/migrations/028. */
export type IngestStatus = 'uploaded' | 'analyzing' | 'ready' | 'failed';

/** Версия документа с прогрессом анализа (GET /projects/{id}/documents). */
export interface DocumentVersion {
  version_id: string;
  document_id: string;
  version_seq: number;
  content_hash: string;
  page_count: number | null;
  format: string;
  title: string;
  ingest_status: IngestStatus;
  ingest_error: string | null;
  ingest_started_at: string | null;
  ingest_finished_at: string | null;
  created_at: string | null;
  /** Нормализованный stem (lower, без расширения). NULL если пусто. */
  title_stem?: string | null;
  /** ID другого document с тем же stem (пара PDF↔DXF/DWG). */
  sibling_document_id?: string | null;
  /** version_id пары после успешного анализа обеих сторон. */
  sibling_version_id?: string | null;
  /** Если в группе разные форматы (PDF+DXF/DWG) — например ['DXF','PDF']. Пусто если пары нет. */
  combined_formats?: string[];
}

export interface DocumentsResponse {
  project_id: string;
  documents: DocumentVersion[];
}

/** Важность замечания. Backend возвращает с большой буквы; в UI нормализуем в lowercase. */
export type Severity = 'Critical' | 'High' | 'Medium' | 'Low' | 'critical' | 'high' | 'medium' | 'low';
export type SeverityLower = 'critical' | 'high' | 'medium' | 'low';

/** Тип дефекта: S — дефект решения, E — пробел комплектности. */
export type DefectType = 'S-defect' | 'E-gap' | null;

/** Статус замечания (вычисляется на backend для dashboard endpoint). */
export type RemarkStatus = 'open' | 'awaiting_response' | 'closed';

/** Пилотный TEXT-маркер remarks.status. NULL = «без маркера». */
export type RemarkPilotStatus =
  | 'archived'
  | 'in_progress'
  | 'awaiting_info'
  | 'ready_review'
  | 'blocked'
  | null;

/** GIP-канбан: 4 статуса, видимые роли gip. archived управляется отдельно куратором. */
export const GIP_STATUSES: Array<{ id: Exclude<RemarkPilotStatus, 'archived' | null>; label: string; color: string }> = [
  { id: 'in_progress',   label: 'В работе',           color: '#F59E0B' },
  { id: 'awaiting_info', label: 'Жду уточнения',      color: '#6366F1' },
  { id: 'ready_review',  label: 'Готово к проверке',  color: '#10B981' },
  { id: 'blocked',       label: 'Заблокировано',      color: '#EF4444' },
];

export interface NotificationItem {
  event_id: string;
  response_id: string;
  remark_id: string;
  project_id: string;
  project_name: string | null;
  remark_body: string | null;
  section_code: string | null;
  severity: string | null;
  draft_source: string | null;
  submitted_at: string | null;
  type: 'new_response';
}

export interface NotificationsResponse {
  items: NotificationItem[];
  count: number;
}

export interface Response {
  id: string;
  body: string;
  evidence?: unknown;
  created_at: string | null;
  status?: 'pending' | 'accepted' | 'rejected';
  reviewed_by?: string | null;
  reviewed_at?: string | null;
}

export interface Remark {
  id: string;
  snapshot_id: string;
  body: string;
  specialist_role: string | null;
  severity: Severity;
  parent_remark_id?: string | null;
  closed_at?: string | null;
  created_at: string | null;
  section_code?: string | null;
  aspect?: string | null;
  defect_type?: DefectType;
  expert_name?: string | null;
  affects_sections?: string[];
  cited_norms?: string[];
  response_count?: number;
  responses?: Response[];
  /**
   * Вычисляемый сервером lifecycle-статус (см. app/main.py remarks_dashboard):
   *   open → нет ответов; awaiting_response → есть ответ, не закрыто; closed → closed_at set.
   * Это НЕ то же самое, что `status` (пилотный TEXT-маркер ниже).
   * Куратор не может писать в это поле напрямую — только через POST /close
   * либо ответы / прочее изменение состояния.
   */
  remark_status?: RemarkStatus;
  domain?: string | null;
  /**
   * Пилотный TEXT-маркер remarks.status (см. RemarkPilotStatus).
   * Управляется ГИПом через PATCH /projects/{pid}/remarks/{rid} { status }.
   * Не путать с `remark_status` выше.
   */
  status?: RemarkPilotStatus;
  /**
   * Стадия замечания (миграция 050): 'acceptance' — приёмка Заказчиком до
   * экспертизы; 'expertise' — экспертиза (дефолт). Задаётся сервером по роли
   * автора при создании.
   */
  stage?: 'acceptance' | 'expertise' | null;
}

export interface RemarksDashboardResponse {
  project_id: string;
  summary: {
    total: number;
    open: number;
    awaiting_response: number;
    closed: number;
    critical: number;
    s_defects: number;
    e_gaps: number;
  };
  remarks: Remark[];
}

/** POST /projects/{id}/remarks body. */
export interface RemarkCreate {
  snapshot_id: string;
  body: string;
  section_id?: string | null;
  specialist_role?: string | null;
  severity?: 'Critical' | 'High' | 'Medium' | 'Low';
  parent_remark_id?: string | null;
  document_version_id?: string | null;
  domain?: string | null;
  assignee_role?: string | null;
  page?: number | null;
}

export interface ResponseCreate {
  body: string;
  evidence?: unknown;
}

/** Нормализуем severity к lowercase для рендера в UI. */
export function normSeverity(s: Severity | undefined | null): SeverityLower {
  const v = (s || 'medium').toLowerCase();
  if (v === 'critical' || v === 'high' || v === 'medium' || v === 'low') return v;
  return 'medium';
}
